/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, Fragment, useCallback, useEffect, useState } from 'react';

import { EngagementSpotlight, useCoordination } from '@atlassiansox/engagekit-ts';
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { mergeRefs } from 'use-callback-ref';

import { PrimaryButton, type PrimaryButtonProps } from '@atlaskit/atlassian-navigation';
import { type ButtonProps } from '@atlaskit/button';
import { type IconButtonProps } from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { RovoIcon } from '@atlaskit/logo';
import { TopNavButton } from '@atlaskit/navigation-system/experimental/top-nav-button';
import { SpotlightManager, SpotlightPulse, SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import Tooltip, { type TooltipProps } from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation';
import {
	ConversationAssistantOnboardingSpotlight,
	SPOTLIGHT_ONBOARDING_TARGET,
	useConversationAssistantSpotlightActions,
	useConversationAssistantSpotlightState,
	useCoordinationClient,
	usePostOfficeCoordination,
} from '@atlassian/conversation-assistant-onboarding';

import {
	ConditionalWrapper,
	type ConditionalWrapperChildren,
} from '../../common/ui/conditional-wrapper';

import { messages } from './messages';

type Props = {
	testId?: string;
	isActive: boolean;
	isDisabled?: boolean;
	onClick: ButtonProps['onClick'];
	tooltipContent?: React.ReactNode;
	shortcut?: TooltipProps['shortcut'];
	siteId: string;
	productKey: string;
};

const defaultButtonStyles = {
	paddingInline: token('space.100'),
};

const inactiveButtonStyles = {
	background: token('color.background.neutral'),
};

const disabledButtonStyles = {
	background: token('color.background.disabled'),
	color: token('color.text.disabled'),
};

type ButtonComponentProps = {
	onMouseEnter?: React.MouseEventHandler;
	style?: React.CSSProperties;
	iconBefore?: React.ReactChild;
	testId?: string;
	isSelected?: boolean;
	onClick?: PrimaryButtonProps['onClick'];
	isDisabled?: boolean;
	tooltip?: React.ReactNode;
	shortcut?: TooltipProps['shortcut'];
	children: React.ReactNode;
	interactionName?: string;
};

type HTMLElementForwardedRefComponent = React.ForwardRefExoticComponent<
	ButtonComponentProps & React.RefAttributes<HTMLElement>
>;
type HTMLButtonElementForwardedRefComponent = React.ForwardRefExoticComponent<
	ButtonComponentProps & React.RefAttributes<HTMLButtonElement>
>;

const getConversationAssistantButton = (
	Button: HTMLButtonElementForwardedRefComponent | HTMLElementForwardedRefComponent = PrimaryButton,
) =>
	forwardRef(
		(
			{
				testId,
				isActive,
				isDisabled = false,
				onClick: onClickProp,
				tooltipContent,
				shortcut,
				siteId,
				productKey,
			}: Props,
			ref: React.Ref<HTMLButtonElement>,
		) => {
			di(useCoordination, useCoordinationClient, FeatureGates);
			const { formatMessage } = useIntl();
			const { start } = useConversationAssistantSpotlightActions();
			const [shouldShowMessage, setShouldShowMessage] = useState(false);
			const messageId =
				productKey === 'confluence'
					? 'confluence_rovo_chat_agents_spotlight_ga'
					: productKey === 'jira'
						? 'jira_rovo_chat_agents_spotlight_ga'
						: '';
			const shouldUseCoordination = !!messageId;
			const { sendAnalyticsEvent } = useAnalytics();

			const openRovoChat = (
				event: React.MouseEvent<HTMLElement, MouseEvent>,
				analyticsEvent: any,
			) => {
				if (!isActive && onClick) {
					onClick(event, analyticsEvent);
				}
			};

			const onClick = useCallback<NonNullable<typeof onClickProp>>(
				(...params) => {
					sendAnalyticsEvent({
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'rovoChatButton',
						attributes: {
							isActive,
							isDisabled,
							productKey,
						},
					});

					return onClickProp?.(...params);
				},
				[isActive, isDisabled, onClickProp, productKey, sendAnalyticsEvent],
			);

			const interactionName = isActive ? 'close-rovo-chat-nav-button' : 'open-rovo-chat-nav-button';
			const getChildren = () => {
				return (
					<Button
						onMouseEnter={() => {
							if (shouldShowMessage) {
								sendAnalyticsEvent({
									eventType: 'ui',
									action: 'hovered',
									actionSubject: 'rovoChatButtonSpotlight',
								});
								start();
							}
						}}
						testId={testId}
						isSelected={isActive}
						onClick={onClick}
						isDisabled={isDisabled}
						tooltip={
							tooltipContent ?? <Fragment>{formatMessage(messages.rovoChatButtonLabel)}</Fragment>
						}
						shortcut={shortcut}
						style={{
							/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */
							...defaultButtonStyles,
							...(!isActive && inactiveButtonStyles),
							...(isDisabled && disabledButtonStyles),
							/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */
						}}
						iconBefore={<RovoIcon size="xxsmall" />}
						interactionName={interactionName}
						ref={ref}
					>
						{formatMessage(messages.rovoChatButtonLabel)}
					</Button>
				);
			};

			return shouldUseCoordination ? (
				<SpotlightWrapper
					setShouldShowMessage={setShouldShowMessage}
					messageId={messageId}
					siteId={siteId}
					children={getChildren()}
					shouldShowMessage={shouldShowMessage}
					openRovoChat={openRovoChat}
				/>
			) : (
				getChildren()
			);
		},
	);

const Nav4Button = forwardRef(
	(
		{ tooltip, shortcut, ...props }: ButtonComponentProps,
		forwardedRef: React.Ref<HTMLButtonElement>,
	) => {
		return (
			<Tooltip
				content={tooltip}
				shortcut={shortcut}
				// This button's visible text matches the tooltip
				// Announcing the tooltip means 'Chat' would be announced twice
				isScreenReaderAnnouncementDisabled
			>
				{({ ref: tooltipRef, ...triggerProps }) => (
					<TopNavButton
						interactionName={props?.interactionName}
						ref={mergeRefs([forwardedRef, tooltipRef])}
						{...triggerProps}
						{...props}
						onClick={(event, analyticsEvent) => {
							props.onClick?.(event, analyticsEvent);
							triggerProps.onClick?.(event);
						}}
						iconBefore={(() => props.iconBefore || null) as IconButtonProps['icon']}
					/>
				)}
			</Tooltip>
		);
	},
);

export const ConversationAssistantButton = getConversationAssistantButton(PrimaryButton);
export const ConversationAssistantButtonNav4 = getConversationAssistantButton(Nav4Button);

export const SpotlightWrapper = ({
	children,
	messageId,
	siteId,
	setShouldShowMessage,
	shouldShowMessage,
	openRovoChat,
}: {
	children: ConditionalWrapperChildren;
	messageId: string;
	siteId: string;
	setShouldShowMessage: React.Dispatch<React.SetStateAction<boolean>>;
	shouldShowMessage: boolean;
	openRovoChat: (event: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: any) => void;
}) => {
	const { activeSpotlight } = useConversationAssistantSpotlightState();
	const coordinationClient = useCoordinationClient(siteId);
	const [onboardingStarted, stopOnboarding] = usePostOfficeCoordination(
		coordinationClient,
		messageId,
		fg('rovo-chat-agents-spotlight-post-office-migration'),
	);
	const { sendAnalyticsEvent } = useAnalytics();

	const onDismissMessage = () => {
		setShouldShowMessage(false);
		stopOnboarding();
	};

	useEffect(() => {
		if (shouldShowMessage && activeSpotlight !== null) {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'shown',
				actionSubject: 'rovoChatButtonSpotlight',
			});
		}
	}, [shouldShowMessage, activeSpotlight, sendAnalyticsEvent]);

	useEffect(() => {
		if (onboardingStarted) {
			setShouldShowMessage(true);
		}
	}, [onboardingStarted, setShouldShowMessage]);
	return (
		<ConditionalWrapper
			condition={shouldShowMessage}
			wrapper={(children) => (
				<SpotlightManager blanketIsTinted>
					<SpotlightTarget name={SPOTLIGHT_ONBOARDING_TARGET}>
						<SpotlightPulse radius={3} pulse={activeSpotlight === null}>
							{children}
							<ConversationAssistantOnboardingSpotlight
								onDismiss={onDismissMessage}
								openRovoChat={openRovoChat}
							/>
							<EngagementSpotlight engagementId={SPOTLIGHT_ONBOARDING_TARGET} />
						</SpotlightPulse>
					</SpotlightTarget>
				</SpotlightManager>
			)}
		>
			{children}
		</ConditionalWrapper>
	);
};
